import { useCallback, useContext, useMemo } from 'react';
import { CurrentUserContentSettingsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/CurrentUserContentSettingsProvider';
import isDefined from '../../../isDefined';
import buildFilterInput from '../../../utils/buildFilterInput';
import mergeFilterInputs from '../../../mergeFilterInputs';
import { MappedUserContentSettingsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/MappedUserContentSettingsProvider';
import filterTypeCheckers from '../../../components/FilterPlateForm/filterTypeCheckers';
import _ from 'lodash';

type EntityFilterBuildingParams =
  | undefined
  | { hasNoFilter: true }
  | {
      hasNoFilter: false;
      primaryField: string;
      primaryFieldValue: string;
      additionalPrimaryFieldValues: string[];
      additionalFilters: FixedFilterPlate[];
    };

const useFilters = (
  selectedEntity: EntityDetails.SelectedEntity | undefined,
  entity: EntityDetails.Entity | undefined,
) => {
  const { contentSettings } = useContext(CurrentUserContentSettingsContext);
  const { selectedMappedUser, isMappedUserForCurrentEntity } = useContext(
    MappedUserContentSettingsContext,
  );

  /**
   * Abstract away the complexity of knowing which filters to use due to the
   * preview mode
   */
  const baseAdditionalFilters = useMemo(() => {
    if (!entity) {
      return [];
    }

    if (selectedMappedUser) {
      return selectedMappedUser.additionalFilters;
    }

    if (contentSettings.mode === 'engagement') {
      const mapping = contentSettings.mappings.find(
        (m) => m.entityId === entity.id,
      );
      if (!mapping) {
        return [];
      }
      return mapping.additionalFilters || [];
    }

    return [];
  }, [contentSettings, entity, selectedMappedUser]);

  /**
   * If we simply merge the {@link FilterInput} derived from the primaryField
   * with any additionalFilters against the same field, we will effectively
   * enter into an AND, while we really want to OR
   *
   * Therefore, we need to extract out any plates against the primary field
   * so that we create a single keywordFilter against the primary field.
   */
  const additionalPrimaryFieldValues = useMemo(() => {
    if (!entity) {
      return [];
    }

    return baseAdditionalFilters.reduce((acc, plate) => {
      if (
        plate.type === 'Fixed' &&
        plate.fixedValue.field === entity.primaryField
      ) {
        const { fixedValue } = plate;
        if (!filterTypeCheckers.isTextFilter(fixedValue)) {
          return acc;
        }

        return _.uniq([...acc, ...fixedValue.keywordValues]);
      }
      return acc;
    }, [] as string[]);
  }, [baseAdditionalFilters, entity]);

  /**
   * Due to extracting out additionalPrimaryFieldValues, we now need to
   * use a subset of additional filters, where we remove any FilterPlates
   * which target the primary entity field
   */
  const additionalFilters = useMemo(() => {
    if (!entity) {
      return baseAdditionalFilters;
    }

    return baseAdditionalFilters.filter(
      (f) => f.fixedValue.field !== entity.primaryField,
    );
  }, [baseAdditionalFilters, entity]);

  const filters = useMemo((): EntityFilterBuildingParams => {
    if (isMappedUserForCurrentEntity) {
      if (!selectedMappedUser) {
        /**
         * This state occurs when the user has selected "None" as their
         * mapped user to preview
         */
        return {
          hasNoFilter: true,
        };
      }

      if (!entity) {
        return undefined;
      }

      return {
        hasNoFilter: false,
        primaryField: entity.primaryField,
        primaryFieldValue: selectedMappedUser.filterValue,
        additionalPrimaryFieldValues,
        additionalFilters,
      };
    }

    if (!selectedEntity) {
      // This is a loading state
      return undefined;
    }

    /**
     * A mapped user should be able to open up a different entity app
     * in a new tab via a drill-down flow.
     * In this case, we do not want to apply contentSettings filters
     */
    if (isMappedUserForCurrentEntity && contentSettings.mode === 'engagement') {
      const mapping = contentSettings.mappings.find(
        (m) => !!entity && m.entityId === entity.id,
      );
      if (!mapping) {
        const e = new Error(`Entity: ${entity ? entity.id : '?'}`);
        e.name = 'Engagement Portal: Content mapping not found';
        throw e;
      }
      return {
        hasNoFilter: false,
        primaryField: selectedEntity.type.primaryField,
        primaryFieldValue: mapping.primaryFieldValue,
        additionalPrimaryFieldValues,
        additionalFilters,
      };
    }
    return {
      hasNoFilter: false,
      primaryField: selectedEntity.type.primaryField,
      primaryFieldValue: selectedEntity.fieldValue,
      additionalPrimaryFieldValues: [],
      additionalFilters: [],
    };
  }, [
    additionalFilters,
    additionalPrimaryFieldValues,
    contentSettings,
    entity,
    isMappedUserForCurrentEntity,
    selectedEntity,
    selectedMappedUser,
  ]);

  return filters;
};

/**
 * Originally, this was intended to just build a filter where we want to filter
 * for a single value against some term such as driver, customer, sales rep, etc
 *
 * We still require this flow for "entity drilldown" flows for execute users
 * in post-portal customers and across all users in pre-portal customers.
 * This also powers the {@link GlobalSearch} flow
 *
 * In an {@link EngagementPortal} we are now also hijacking this flow to
 * optionally filter content by a collection of {@link MappedUser}s for portal
 * administrators, so they can preview what a
 * {@link UserManagement.EngagementContentSettings} user's data is.
 */
const useSelectedEntityFilter = (
  selectedEntity: EntityDetails.SelectedEntity | undefined,
  entity: EntityDetails.Entity | undefined,
) => {
  const filters = useFilters(selectedEntity, entity);

  const buildEntityFilter = useCallback(():
    | {
        entityFilter: FilterInput;
        entityFilterAsFilterPlates: FilterPlateType[];
      }
    | undefined => {
    if (!filters) {
      return undefined;
    }
    if (filters.hasNoFilter) {
      return {
        entityFilter: {},
        entityFilterAsFilterPlates: [],
      };
    }

    const {
      primaryField,
      primaryFieldValue,
      additionalPrimaryFieldValues,
      additionalFilters,
    } = filters;

    const fi: FilterInput = mergeFilterInputs(
      {
        keywords: [
          {
            field: primaryField,
            values: [primaryFieldValue, ...additionalPrimaryFieldValues].filter(
              isDefined,
            ),
          },
        ],
      },
      buildFilterInput({
        scopes: [...additionalFilters.map((f) => f.fixedValue)],
        drillDowns: [],
      }),
    );

    const asFilterPlates: FixedFilterPlate[] = [
      {
        id: `entity-filter`,
        type: 'Fixed',
        fixedValue: {
          keywordValues: [primaryFieldValue, ...additionalPrimaryFieldValues],
          field: primaryField,
          id: 'entity-filter-fixed-value',
          fieldType: 'text',
          mode: 'plate',
        },
      },
      ...additionalFilters,
    ];

    return {
      entityFilter: fi,
      entityFilterAsFilterPlates: asFilterPlates,
    };
  }, [filters]);

  const selectedEntityFilter = useMemo(
    () => buildEntityFilter(),
    [buildEntityFilter],
  );

  return selectedEntityFilter;
};

export default useSelectedEntityFilter;
